/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import RouterService from '@ember/routing/router-service';
import Transition from '@ember/routing/-private/transition';
import { tracked } from '@glimmer/tracking';
import { task, timeout } from 'ember-concurrency';
import { action } from '@ember/object';
import { isArray } from '@ember/array';
import { acceptedFileTypes } from 'cio/utils/images';
import { UploadStatus, FileUploadMeta, ErrorMessage } from 'cio/utils/asset-library';
import config from 'cio/config/environment';

interface AssetLibraryBulkUploadArgs {
  readOnly: boolean;
  folderId: string | null;
  dropFilesToUpload?: File[];

  onClickUploadButton?: () => unknown;
  onFileUploadStarted?: (folderId?: string | null) => unknown;
  onAllFilesUploadFinished?: () => unknown;
}

export default class AssetLibraryBulkUpload extends Component<AssetLibraryBulkUploadArgs> {
  @service store!: DS.Store;
  @service router!: RouterService;

  readonly acceptedFileTypes = `${acceptedFileTypes},application/pdf`;

  @tracked uploads: FileUploadMeta[] = [];
  @tracked previousBatchUploadsCount = 0;
  @tracked hideProgressBar = true;

  @tracked showConfirmTransitionModal = false;
  userAllowedTransitionWhileUploading = false;
  transition: Transition | null = null;

  constructor(owner: unknown, args: AssetLibraryBulkUploadArgs) {
    super(owner, args);
    window.addEventListener('beforeunload', this.handleWindowUnload);
    this.router.on('routeWillChange', this.checkIfTransitionAllowed);
  }

  willDestroy(): void {
    super.willDestroy();
    // @ts-ignore = Ember 3 doesn't (officially) support router.off, but Ember 4
    // does. Leave this ts-ignore in until we upgrade.
    this.router.off?.('routeWillChange', this.checkIfTransitionAllowed);
    window.removeEventListener('beforeunload', this.handleWindowUnload);
  }

  get uploadItemsTotal(): number {
    return this.uploads.length;
  }

  get isUploading(): boolean {
    return this.uploads.some(
      (uploadMeta) => uploadMeta.status === UploadStatus.Pending || uploadMeta.status === UploadStatus.Uploading,
    );
  }

  get isDisabled() {
    return this.uploadTask.isRunning || this.args.readOnly;
  }

  @action
  onFileUploadStarted(folderId: string | null) {
    this.hideProgressBar = false;
    this.args.onFileUploadStarted?.(folderId);
  }

  @action
  onAllFilesUploadFinished() {
    if (this.isUploading) {
      return;
    }

    this.args.onAllFilesUploadFinished?.();
    this.maybeResetAndHideProgressBar.perform();
  }

  maybeResetAndHideProgressBar = task({ drop: true }, async () => {
    // These delays are solely for visual purposes.
    if (!this.isUploading) {
      // Display a full 100% progress bar a second after all the uploads are finished.
      await timeout(config.APP.filesUploadProgressBarEaseOutDuration);
      this.hideProgressBar = true;
      // Reset the progress bar after the "hide" animation is played for another second.
      await timeout(config.APP.filesUploadProgressBarEaseOutDuration);
      this.previousBatchUploadsCount = this.uploadItemsTotal;
    }
  });

  @action
  onClickUploadButton() {
    this.args.onClickUploadButton?.();
  }

  @action
  clearUploadsQueue() {
    this.uploads = [];
    this.previousBatchUploadsCount = 0;
  }

  @action
  checkIfTransitionAllowed(transition: Transition) {
    if (
      this.isUploading &&
      !this.userAllowedTransitionWhileUploading &&
      !transition.to.find((route) => route.name === this.router.currentRouteName)
    ) {
      transition.abort();
      this.transition = transition;
      this.showConfirmTransitionModal = true;
    }
  }

  @action
  onConfirmTransition() {
    this.userAllowedTransitionWhileUploading = true;
    this.transition?.retry();
    this.showConfirmTransitionModal = false;
  }

  @action
  handleWindowUnload(evt: Event) {
    if (this.isUploading) {
      evt.preventDefault();
      // Technically `returnValue` is deprecated, but still advised for this specific use case:
      // https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
      return ((evt as any).returnValue = 'Your files are still uploading');
    }
    return;
  }

  @action
  onDropFilesToUploadChange() {
    this.args.dropFilesToUpload?.forEach((f) => this.enqueueUploadTask.perform(f));
  }

  enqueueUploadTask = task({ enqueue: true }, async (file: File) => {
    let uploadMeta: FileUploadMeta = {
      file,
      folderId: this.args.folderId,
      status: UploadStatus.Pending,
    };
    this.uploads = [...this.uploads, uploadMeta];

    this.uploadTask.perform(uploadMeta);
  });

  uploadTask = task({ enqueue: true }, async (uploadMeta: FileUploadMeta) => {
    let { file, folderId } = uploadMeta;

    let errors = this.validateImage(file);
    if (errors.length > 0) {
      uploadMeta.errorMessages = errors;
      uploadMeta.status = UploadStatus.Error;
      this.updateUploads(uploadMeta);
      this.onAllFilesUploadFinished();
      return;
    }

    let newAsset = this.store.createRecord('asset', {
      name: file.name,
      isDir: false,
      size: file.size,
      parentId: folderId,
    });

    this.onFileUploadStarted(folderId);

    try {
      uploadMeta.status = UploadStatus.Uploading;
      this.updateUploads(uploadMeta);

      await newAsset.upload(file);

      uploadMeta.status = UploadStatus.Success;
    } catch (err) {
      newAsset.unloadRecord();

      uploadMeta.status = UploadStatus.Error;
      if (isArray(err?.errors)) {
        uploadMeta.errorMessages = [{ message: err.errors[0].detail }];
      }
    }

    this.updateUploads(uploadMeta);
    this.onAllFilesUploadFinished();
  });

  private updateUploads(updatedUploadMeta: FileUploadMeta) {
    let uploads: FileUploadMeta[] = [];

    for (let uploadMeta of this.uploads) {
      if (uploadMeta.file === updatedUploadMeta.file) {
        let { file, folderId, status, errorMessages } = updatedUploadMeta;
        uploads.push({ file, folderId, status, errorMessages });
      } else {
        uploads.push(uploadMeta);
      }
    }

    this.uploads = uploads;
  }

  private validateImage(image: { type: string }): ErrorMessage[] {
    if (image == null) {
      return [{ message: 'Image is invalid.' }];
    }

    let errors = [];
    if (!this.acceptedFileTypes.includes(image.type)) {
      errors.push({ message: 'File type is not supported.' });
    }

    return errors;
  }
}
